import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  TextField,
  Box,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Snackbar
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const CustomTextField = ({ ...props }) => (
  <TextField
    fullWidth
    variant="outlined"
    {...props}
    sx={{
      '& .MuiOutlinedInput-root': {
        color: '#F5F7FA',
        bgcolor: 'rgba(255, 255, 255, 0.03)',
        '& fieldset': { borderColor: '#334155' },
        '&:hover fieldset': { borderColor: '#475569' },
        '&.Mui-focused fieldset': { borderColor: '#3B82F6' }
      },
      '& .MuiInputLabel-root': { color: '#94A3B8' },
      '& .MuiInputLabel-root.Mui-focused': { color: '#3B82F6' },
      ...props.sx
    }}
  />
);

const Settings = () => {
  const { currentUser, updateUserPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    darkMode: true, // Default to true given the theme
    twoFactorAuth: false
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '', // Note: Re-auth flow is ideal, but basic update supported if session active
    newPassword: '',
    confirmPassword: ''
  });

  const handleSettingChange = (setting) => (event) => {
    setSettings(prev => ({ ...prev, [setting]: event.target.checked }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSnackbar({ open: true, message: 'New passwords do not match', severity: 'error' });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setSnackbar({ open: true, message: 'Password must be at least 6 characters', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      await updateUserPassword(passwordForm.newPassword);
      setSnackbar({ open: true, message: 'Password updated successfully', severity: 'success' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error updating password:', error);
      let msg = 'Failed to update password. You may need to re-login.';
      if (error.code === 'auth/requires-recent-login') {
        msg = 'Security check: Please logout and login again to change your password.';
      }
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      // Logic for delete would usually go here
      // await deleteUser(currentUser);
      setDeleteDialogOpen(false);
      setSnackbar({ open: true, message: 'Feature not fully enabled in demo.', severity: 'info' });
    } catch (error) {
      console.error('Error deleting account:', error);
      setSnackbar({ open: true, message: 'Failed to delete account', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0A0E17', py: 8 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#0F141F', border: '1px solid #1E293B', borderRadius: 2, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#F5F7FA' }}>
                  <NotificationsIcon sx={{ mr: 1, color: '#3B82F6' }} />
                  Notifications
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary={<Typography color="#F5F7FA">Email Updates</Typography>}
                      secondary={<Typography variant="caption" color="#94A3B8">Receive daily digests</Typography>}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.emailNotifications}
                        onChange={handleSettingChange('emailNotifications')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#3B82F6' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3B82F6' },
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={<Typography color="#F5F7FA">Push Alerts</Typography>}
                      secondary={<Typography variant="caption" color="#94A3B8">Browser notifications</Typography>}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.pushNotifications}
                        onChange={handleSettingChange('pushNotifications')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#3B82F6' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3B82F6' },
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card sx={{ bgcolor: '#0F141F', border: '1px solid #1E293B', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#F5F7FA' }}>
                  <PaletteIcon sx={{ mr: 1, color: '#00E676' }} />
                  Appearance
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary={<Typography color="#F5F7FA">Dark Mode</Typography>}
                      secondary={<Typography variant="caption" color="#94A3B8">Always on for premium feel</Typography>}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        disabled
                        checked={true}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#00E676' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00E676' },
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ bgcolor: '#0F141F', border: '1px solid #1E293B', borderRadius: 2 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#F5F7FA', mb: 3 }}>
                  <SecurityIcon sx={{ mr: 1.5, color: '#F59E0B' }} />
                  Security Settings
                </Typography>

                <Box component="form" onSubmit={handleUpdatePassword}>
                  <Grid container spacing={3}>
                    {/* Note: Current Password field often needed for Re-Auth. 
                        For this implementation, we rely on 'recent login' status of session. */}

                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        type="password"
                        label="New Password"
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        helperText="Min. 6 characters"
                        FormHelperTextProps={{ sx: { color: '#64748B' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        type="password"
                        label="Confirm New Password"
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                      />
                    </Grid>
                  </Grid>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading || !passwordForm.newPassword}
                    sx={{
                      mt: 4,
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      fontWeight: 600,
                      px: 4
                    }}
                  >
                    Update Password
                  </Button>
                </Box>

                <Divider sx={{ my: 6, borderColor: '#1E293B' }} />

                <Box>
                  <Typography variant="h6" color="#EF4444" gutterBottom>
                    Danger Zone
                  </Typography>
                  <Typography variant="body2" color="#94A3B8" sx={{ mb: 2 }}>
                    Once you delete your account, there is no going back. Please be certain.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete Account
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: { bgcolor: '#1E293B', color: '#F5F7FA' }
          }}
        >
          <DialogTitle>Delete Account?</DialogTitle>
          <DialogContent>
            <Typography color="#B0B3C0">
              Are you sure you want to delete your account? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#94A3B8' }}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              color="error"
              disabled={loading}
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Settings;
